/* eslint-disable react/react-in-jsx-scope */
import { useMutation } from "@apollo/client";
import { CircularProgress } from "@material-ui/core";
import "@phuocng/react-pdf-viewer/cjs/react-pdf-viewer.css";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { IAdvantageModal } from "domains/CashManagement/types";
import { useAdvantagesFormStyles } from "domains/Fidelity/style";
import { USE_ADVANTAGE } from "graphql/Fidelity/mutations";
import { GET_ADVANTAGE_PROGRAM_FOR_CLIENT } from "graphql/Fidelity/queries";
import { BadgeIcon } from "icons";
import React, { createRef, memo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button } from "ui/Button";
import Dialog from "ui/Dialog";
import { TextField } from "ui/TextField";
import { formatAmount, formatDate } from "utils";
import { isSuccess } from "utils/https";

const StyledInput = styled(TextField)`
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 6px;
  }
`;
function UseProgramDialog({ open, onClose, endDate, advantage, clientId }: IAdvantageModal) {
  const { t } = useTranslation();
  const classes = useAdvantagesFormStyles();
  const { text } = useText();
  const [quantity, setQuantity] = useState(advantage?.quantity);
  const setAlert = useAlertDispatch();
  const [useAdvantage, useAdvantageData] = useMutation(USE_ADVANTAGE);
  const inputRef = createRef();

  const isOkToSave = React.useMemo(() => quantity > advantage?.quantity || quantity < 1, [quantity]);

  const handleChange = (event: React.ChangeEvent<any>) => {
    const $quantity = +event?.target?.value;
    if ($quantity > advantage?.remainQuantity || $quantity < 0) return;
    setQuantity($quantity);
  };

  React.useEffect(() => {
    setQuantity(advantage?.remainQuantity > 0 ? advantage?.remainQuantity : 0);
  }, [advantage]);

  const handleUseAdvantage = () => {
    useAdvantage({
      variables: {
        input: {
          clientId,
          advantageId: advantage?._id,
          quantity,
        },
      },
      refetchQueries: [
        {
          query: GET_ADVANTAGE_PROGRAM_FOR_CLIENT,
          variables: {
            params: {
              clientId,
              programId: advantage?.program,
            },
          },
        },
      ],
    }).then(({ data }) => {
      if (isSuccess(data)) {
        setAlert({
          severity: "success",
          message: text.fidelity_program.use_advantage_success_message,
        });
        onClose();
      } else {
        setAlert({
          severity: "error",
          message: text.all.error_occurred,
        });
      }
    });
  };

  const onIncrease = () => {
    if (quantity === advantage?.remainQuantity) return;
    setQuantity(quantity + 1);
  };

  const onDecrease = () => {
    if (quantity <= 1) return;
    setQuantity(quantity - 1);
  };

  return (
    <Dialog
      open={open}
      handleClose={onClose}
      handleConfirm={() => {}}
      maxWidth="sm"
      height="auto"
      hideDialogHeader
      dialogActionButtonCustomized
    >
      <div className={classes.dialogContent}>
        <div className={classes.headerContainer}>
          <BadgeIcon height={130} />
          <div>
            <h2 className={classes.messaging}>{advantage?.service?.label}</h2>
            <h2 className={classes.amount}> {`${advantage?.maxAmount}%`}</h2>
          </div>
        </div>
        <small className={classes.description}>{advantage?.service?.description}</small>
        <div className={classes.dateRange}>
          <p className={classes.date}>
            {text.fidelity_program.ending_periode} : <b>{formatDate(endDate, "dd/MM/YYYY")}</b>{" "}
          </p>
          <p className={classes.date}>
            {text.all.max_amount} : <b>{`${advantage?.maxAmount}%`}</b>
          </p>
        </div>
        <div className={classes.inputContainer}>
          <Button variant="contained" onClick={onDecrease} className={classes.heighOrLowerButton}>
            -
          </Button>
          <div>
            <StyledInput
              fullWidth
              label={text.all.quantity}
              type="number"
              name="from"
              onChange={handleChange}
              value={quantity}
              inputProps={{
                classes: { notchedOutline: classes.noBorder },
                min: 1,
                max: advantage?.remainQuantity,
                style: { textAlign: "center", height: 8 },
              }}
              error={isOkToSave}
            />
            <p className={classes.reminder}>{`/${advantage?.remainQuantity > 0 ? advantage?.remainQuantity : 0}`}</p>
          </div>
          <Button variant="contained" onClick={onIncrease} className={classes.heighOrLowerButton} size="small">
            &#x2B;
          </Button>
        </div>
        {isOkToSave && (
          <small className={classes.error}>
            {t("use_advantage_error_message", {
              quantity: advantage?.remainQuantity > 0 ? advantage?.remainQuantity : 0,
            })}
          </small>
        )}

        <div className={classes.buttonContainer}>
          <Button variant="contained" onClick={onClose} className={classes.cancelButton} size="large">
            {text.all.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={handleUseAdvantage}
            color="primary"
            className={classes.confirmButton}
            size="large"
            disabled={useAdvantageData?.loading || quantity <= 0}
          >
            {text.all.confirm}{" "}
            {useAdvantageData?.loading && <CircularProgress className={classes.progressBar} size={30} />}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export default memo(UseProgramDialog);
